/**
 * Flask Authentication Service
 * Handles silent automatic token refresh using Flask backend API
 */

import { apiBaseUrl } from '../config/env';

const FLASK_API_BASE = apiBaseUrl.replace(/\/$/, '');
const FLASK_LOGIN_ENDPOINT = '/api/auth/login';
const TOKEN_STORAGE_KEY = 'flaskAuthToken';
const TOKEN_EXPIRY_KEY = 'flaskAuthTokenExpiry';

// Hardcoded credentials for automatic login
const FLASK_CREDENTIALS = {
  username: 'admin',
  password: 'adminpass',
};

interface LoginResponse {
  success: boolean;
  token?: string;
  username?: string;
  role?: string;
  requires_2fa?: boolean;
  twofa_method?: string;
  error?: string;
}

export class FlaskAuthService {
  private static isAuthenticating = false;

  /**
   * Check if current token is valid
   */
  static isTokenValid(): boolean {
    const token = this.getToken();
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!token || !expiry) {
      return false;
    }

    // Check if token has expired (with 5-minute buffer)
    const expiryTime = parseInt(expiry, 10);
    return Date.now() < expiryTime - 5 * 60 * 1000;
  }

  /**
   * Get stored token from localStorage
   */
  static getToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  /**
   * Set token and expiry in localStorage
   */
  private static setToken(token: string, expiryHours: number = 24): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    // Calculate expiry: current time + expiryHours
    const expiryTime = Date.now() + expiryHours * 60 * 60 * 1000;
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
  }

  /**
   * Clear stored token
   */
  static clearToken(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }

  /**
   * Perform login with Flask API
   */
  private static async login(): Promise<string> {
    if (this.isAuthenticating) {
      // Prevent multiple simultaneous login attempts
      console.log('[FlaskAuth] Already authenticating, waiting...');
      return new Promise((resolve, reject) => {
        const checkToken = setInterval(() => {
          const token = this.getToken();
          if (token) {
            clearInterval(checkToken);
            resolve(token);
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkToken);
          reject(new Error('Authentication timeout'));
        }, 10000);
      });
    }

    this.isAuthenticating = true;

    try {
      console.log('[FlaskAuth] Attempting automatic login with credentials...');

      const response = await fetch(`${FLASK_API_BASE}${FLASK_LOGIN_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(FLASK_CREDENTIALS),
      });

      const data: LoginResponse = await response.json();

      if (!response.ok || !data.success || !data.token) {
        throw new Error(data.error || 'Login failed');
      }

      console.log('[FlaskAuth] ✅ Login successful! Token obtained.');
      this.setToken(data.token);

      return data.token;
    } catch (error) {
      console.error('[FlaskAuth] ❌ Login failed:', error);
      this.clearToken();
      throw error;
    } finally {
      this.isAuthenticating = false;
    }
  }

  /**
   * Ensure valid token - refresh if needed
   * This is the main entry point for the app
   */
  static async ensureToken(): Promise<string> {
    const currentToken = this.getToken();

    // If valid token exists, return it
    if (currentToken && this.isTokenValid()) {
      console.log('[FlaskAuth] ✅ Valid token found');
      return currentToken;
    }

    // Token missing or expired - perform login
    console.log('[FlaskAuth] ⏳ Token missing or expired, performing automatic login...');
    return this.login();
  }

  /**
   * Get authorization headers for API requests
   * (kept for compatibility — must not send Bearer behind Traefik basic-auth,
   * the node backend injects its own das token server-side)
   */
  static getAuthHeaders(): Record<string, string> {
    return {};
  }

  /**
   * Manual login (if needed)
   */
  static async manualLogin(username: string, password: string): Promise<string> {
    this.isAuthenticating = true;

    try {
      const response = await fetch(`${FLASK_API_BASE}${FLASK_LOGIN_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data: LoginResponse = await response.json();

      if (!response.ok || !data.success || !data.token) {
        throw new Error(data.error || 'Login failed');
      }

      this.setToken(data.token);
      return data.token;
    } finally {
      this.isAuthenticating = false;
    }
  }

  /**
   * Logout
   */
  static logout(): void {
    this.clearToken();
    console.log('[FlaskAuth] Logged out');
  }
}

export default FlaskAuthService;
